import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { websiteLeadService } from '../../api/services/website-leads';
import AuthLayout from '../../components/layout-components/AuthLayout';
import { Button } from '../../components/ui/button';
import { FieldLabel, FormRequiredLegend } from '../../components/ui/field-label';
import { Input } from '../../components/ui/input';

const RequestDemoPage = () => {
  const [hospitalName, setHospitalName] = useState('');
  const [name, setName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [whereStart, setWhereStart] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const next = {};
    if (!hospitalName.trim()) next.hospital_name = 'Hospital name is required.';
    if (!name.trim()) next.name = 'Your name is required.';
    if (!workEmail.trim()) next.work_email = 'Work email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail.trim())) next.work_email = 'Enter a valid work email.';
    if (!whereStart.trim()) next.where_you_would_start = 'Tell us where you would start.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await websiteLeadService.submitDemo({
        hospital_name: hospitalName.trim(),
        name: name.trim(),
        work_email: workEmail.trim(),
        where_you_would_start: whereStart.trim(),
      });
      toast.success(res?.message || 'Demo request submitted.');
      setSubmitted(true);
    } catch (error) {
      const fieldErrors = error?.data?.errors || error?.data?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const next = {};
        Object.entries(fieldErrors).forEach(([key, value]) => {
          next[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setErrors(next);
      }
      toast.error(error?.data?.message || 'Unable to submit demo request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Request received"
        description="Thank you. Our team has received your demo request and will follow up shortly."
        footer={
          <p className="text-center text-sm text-ink-600">
            <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
              Back to login
            </Link>
          </p>
        }
      >
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          A PeerMed administrator has been notified. We will contact you at the work email you provided.
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Request a demo"
      description="Tell us about your hospital. We will follow up to schedule a PeerMed Hospital demo."
      footer={
        <p className="text-center text-sm text-ink-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
            Back to login
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <FormRequiredLegend />
        <div>
          <FieldLabel required htmlFor="demo-hospital">Hospital name</FieldLabel>
          <Input
            id="demo-hospital"
            className="mt-1"
            placeholder="Institution and city"
            value={hospitalName}
            onChange={(e) => setHospitalName(e.target.value)}
          />
          {errors.hospital_name && <p className="mt-1 text-xs text-danger">{errors.hospital_name}</p>}
        </div>
        <div>
          <FieldLabel required htmlFor="demo-name">Your name</FieldLabel>
          <Input
            id="demo-name"
            className="mt-1"
            placeholder="Dr."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
        </div>
        <div>
          <FieldLabel required htmlFor="demo-email">Work email</FieldLabel>
          <Input
            id="demo-email"
            className="mt-1"
            type="email"
            placeholder="you@hospital.org"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={workEmail}
            onChange={(e) => setWorkEmail(e.target.value)}
          />
          {errors.work_email && <p className="mt-1 text-xs text-danger">{errors.work_email}</p>}
        </div>
        <div>
          <FieldLabel required htmlFor="demo-where">Where you would start</FieldLabel>
          <textarea
            id="demo-where"
            className="mt-1 min-h-24 w-full rounded-md border border-parch-200 bg-white px-3 py-2.5 text-sm text-ink-900 outline-none placeholder:text-ink-400 focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            placeholder="Department, number of beds, deployment preference"
            value={whereStart}
            onChange={(e) => setWhereStart(e.target.value)}
          />
          {errors.where_you_would_start && (
            <p className="mt-1 text-xs text-danger">{errors.where_you_would_start}</p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Request a demo'}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default RequestDemoPage;
